import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Camera, CreditCard, CheckCircle, LinkSimple, Trash, ShieldCheck } from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function LinkCard() {
  const nav = useNavigate();
  const [cards, setCards] = useState([]);
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);

  const load = () => api.get("/customer/cards").then((r) => setCards(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const lookup = async (token) => {
    const t = (token || "").trim();
    if (!t) return toast.error("Scan or paste your card QR");
    setBusy(true);
    try {
      const { data } = await api.post("/customer/card/lookup", { token: t });
      setFound({ card_reference: data.card_reference, token: t });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmLink = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/customer/card/link", { token: found.token });
      toast.success(`Card ${data.card_reference} linked to your OneKite account`);
      setFound(null); setManual(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const unlink = async (ref) => {
    if (!window.confirm("Unlink this physical card? It will stop working immediately.")) return;
    try {
      await api.post("/customer/card/unlink", { token: ref });
      toast.success("Card unlinked"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const startCamera = () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("card-qr-reader");
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
          async (decoded) => { await stopCamera(); lookup(decoded); }, () => {});
      } catch (e) {
        toast.error("Camera unavailable — paste the code instead");
        setScanning(false);
      }
    }, 100);
  };
  const stopCamera = async () => {
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch { /* already stopped */ }
    scannerRef.current = null; setScanning(false);
  };
  useEffect(() => () => { stopCamera(); }, []);

  return (
    <div className="pt-2 space-y-5">
      <button onClick={() => nav(-1)} data-testid="link-card-back" className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Back</button>

      <div>
        <h1 className="font-display font-bold text-2xl flex items-center gap-2"><CreditCard size={24} className="text-primary" weight="duotone" /> Physical OneKite card</h1>
        <p className="text-sm text-muted-foreground">Link an optional physical card to your account. Your app QR and card both identify the same OneKite ID.</p>
      </div>

      {cards.length > 0 && (
        <div className="card-surface p-5" data-testid="linked-cards">
          <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-3">Your linked cards</p>
          <div className="space-y-2.5">
            {cards.map((c) => (
              <div key={c.id} data-testid="linked-card" className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-xl bg-primary/15 flex items-center justify-center shrink-0"><CreditCard size={16} weight="duotone" className="text-primary" /></span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium font-mono">{c.card_reference}</p>
                  <p className="text-[11px] text-primary flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Active</p>
                </div>
                <button data-testid={`unlink-${c.card_reference}`} onClick={() => unlink(c.card_reference)} className="text-muted-foreground hover:text-red-500"><Trash size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {found ? (
        <div className="card-surface p-6 text-center fade-up" data-testid="card-confirm">
          <div className="h-14 w-14 mx-auto rounded-2xl bg-primary/15 flex items-center justify-center"><CreditCard size={26} weight="duotone" className="text-primary" /></div>
          <p className="text-xs text-muted-foreground mt-4">Genuine, unassigned card</p>
          <p className="font-display font-extrabold text-2xl font-mono mt-1" data-testid="card-confirm-ref">{found.card_reference}</p>
          <p className="text-xs text-muted-foreground mt-3">Link this card to your OneKite account?</p>
          <div className="flex gap-2 mt-5">
            <Button variant="outline" onClick={() => setFound(null)} className="flex-1 h-11 rounded-xl border-black/10" data-testid="card-cancel">Cancel</Button>
            <Button onClick={confirmLink} disabled={busy} className="flex-1 h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="card-link-confirm"><LinkSimple size={18} className="mr-1.5" /> {busy ? "Linking…" : "Link card"}</Button>
          </div>
        </div>
      ) : (
        <>
          {scanning ? (
            <div className="card-surface p-4">
              <div id="card-qr-reader" className="rounded-xl overflow-hidden" />
              <Button data-testid="link-card-stop-camera" variant="outline" onClick={stopCamera} className="w-full mt-3 rounded-xl border-black/10">Stop camera</Button>
            </div>
          ) : (
            <Button data-testid="link-card-camera" onClick={startCamera} className="w-full h-14 rounded-2xl bg-primary/15 text-primary hover:bg-primary/20 font-semibold border border-primary/20">
              <Camera size={22} className="mr-2" /> Scan card QR
            </Button>
          )}

          <div className="card-surface p-5 space-y-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wider font-mono">Or paste card code</p>
            <Input data-testid="link-card-token" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="OKC.…" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono text-xs" />
            <Button data-testid="link-card-lookup" onClick={() => lookup(manual)} disabled={busy} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
              {busy ? "Checking…" : "Check card"}
            </Button>
          </div>
        </>
      )}

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck size={16} className="text-primary" /> Your app QR and physical card always point to the same OneKite account.
      </div>
    </div>
  );
}
